import { useCallback } from 'react';
import Box from 'fe-design-base/atoms/Box';
import Text from 'fe-design-base/atoms/Text';
import Button from 'fe-design-base/molecules/Button';
import Pill from 'fe-design-base/molecules/Pill';

import {
  acceptTradeCoverRequest,
  rejectTradeCoverRequest,
} from 'features/notifications/api';
import { useRequestAction } from 'features/notifications/hooks/useRequestAction';
import { STATUS_UI } from 'features/notifications/statuses';
import { RequestType, TradeCoverClaim } from 'features/notifications/types';

import { toI18n } from 'util/i18n';

import { BaseRequest } from '../../BaseRequest';
import { ShiftDetails } from '../../ShiftDetails';

type CoverProps = {
  request: TradeCoverClaim;
  setSelectedRequest?: (request: TradeCoverClaim, type: RequestType) => void;
};

type CoverStatus = keyof (typeof STATUS_UI)['cover'];

const Cover = ({ request, setSelectedRequest }: CoverProps) => {
  const { isLoading: isAccepting, handleAction: handleAccept } =
    useRequestAction({
      apiFunction: acceptTradeCoverRequest,
      itemId: request.responder_item_id as string,
      successMessage: 'requests_feed.cover.details.accepted_success',
      initiatedFrom: 'cover_shift_card',
      buttonText: toI18n('requests_feed.actions.accept'),
    });
  const { isLoading: isDeclining, handleAction: handleDecline } =
    useRequestAction({
      apiFunction: rejectTradeCoverRequest,
      itemId: request.responder_item_id as string,
      successMessage: 'requests_feed.cover.details.declined_success',
      initiatedFrom: 'cover_shift_card',
      buttonText: toI18n('requests_feed.actions.no_thanks'),
    });
  const {
    shift,
    requester,
    status,
    last_status_changed_at_secs,
    qualifications,
    type,
    pending_approval_count,
    reason,
    location,
    viewer_role,
  } = request;

  const { first_name, last_name, avatar } = requester || {};
  const name = `${first_name} ${last_name}`;

  const iAmTheSellerOrOAM =
    viewer_role === 'oam' || viewer_role === 'requester';
  const iAmTheBuyer = viewer_role === 'responder';

  const normalizedStatus = status as CoverStatus;
  const pillUI = STATUS_UI.cover[normalizedStatus];

  const handleClick = useCallback(() => {
    setSelectedRequest?.(request, type);
  }, [setSelectedRequest, request, type]);

  return (
    <BaseRequest
      name={name}
      updatedAt={last_status_changed_at_secs}
      type={type}
      avatar={avatar}
    >
      <Pill variant={pillUI?.variant}>{pillUI?.label}</Pill>

      {iAmTheSellerOrOAM && (
        <Text
          variant="captionBold"
          uppercase
          i18n="requests_feed.cover.pending_claims"
          i18nProps={{ count: pending_approval_count || 0 }}
        />
      )}

      <ShiftDetails shift={shift} location={location} />

      {qualifications && (
        <>
          <Text
            variant="bodySmBold"
            color="mono700"
            i18n="requests_feed.cover.qualifications"
          />
          <Text variant="bodySm" color="mono700">
            {qualifications}
          </Text>
        </>
      )}

      {reason && (
        <>
          <Text
            variant="bodySmBold"
            color="mono700"
            i18n="requests_feed.cover.reason"
          />
          <Text variant="bodySm" color="mono700">
            {reason}
          </Text>
        </>
      )}

      {iAmTheSellerOrOAM && (
        <Button fullWidth onClick={handleClick}>
          {toI18n('requests_feed.cover.review')}
        </Button>
      )}

      {iAmTheBuyer && normalizedStatus === 'needs_reply' && (
        <Box row gap={8}>
          <Button
            variant="secondary"
            fullWidth
            onClick={handleDecline}
            disabled={isDeclining || isAccepting}
            isLoading={isDeclining}
          >
            {toI18n('requests_feed.actions.no_thanks')}
          </Button>
          <Button
            fullWidth
            onClick={handleAccept}
            disabled={isAccepting || isDeclining}
            isLoading={isAccepting}
          >
            {toI18n('requests_feed.actions.accept')}
          </Button>
        </Box>
      )}

      {iAmTheBuyer && normalizedStatus === 'pending_approval' && (
        <Button
          variant="secondaryDestructive"
          fullWidth
          onClick={handleDecline}
          disabled={isDeclining}
          isLoading={isDeclining}
        >
          {toI18n('requests_feed.actions.undo_accept')}
        </Button>
      )}
    </BaseRequest>
  );
};

export default Cover;
